import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Save, ShieldCheck, RotateCcw, Lock } from "lucide-react";

export default function AdminRoles() {
  const [cfg, setCfg] = useState(null);
  const [perms, setPerms] = useState({});
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/roles").then((r) => {
      setCfg(r.data);
      setPerms(JSON.parse(JSON.stringify(r.data.role_permissions)));
      const first = Object.keys(r.data.roles).find((k) => k !== "super_admin");
      setActive(first);
    }).catch(() => toast.error("Could not load roles"));
  }, []);

  const isSuper = active === "super_admin";
  const has = (p) => (perms[active] || []).includes(p);
  const toggle = (p) => {
    if (isSuper) return;
    setPerms((cur) => {
      const list = new Set(cur[active] || []);
      list.has(p) ? list.delete(p) : list.add(p);
      return { ...cur, [active]: [...list] };
    });
  };
  const resetToDefault = () => {
    if (isSuper) return;
    setPerms((cur) => ({ ...cur, [active]: [...(cfg.defaults[active] || [])] }));
    toast.info(`Reset ${cfg.roles[active]} to defaults (not yet saved)`);
  };
  const save = async () => {
    setSaving(true);
    try {
      const body = { roles: {} };
      Object.keys(cfg.roles).forEach((r) => { if (r !== "super_admin") body.roles[r] = perms[r] || []; });
      const { data } = await api.put("/admin/roles", body);
      setPerms(JSON.parse(JSON.stringify(data.role_permissions)));
      toast.success("Roles & permissions saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!cfg || !active) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div>;

  return (
    <div className="max-w-5xl" data-testid="admin-roles-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-olive" /> Roles &amp; Permissions</h1>
        <Button onClick={save} disabled={saving} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="save-roles-btn">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save Changes</Button>
      </div>
      <p className="text-sm text-stone-500 mb-6">Configure what each internal role can do. Super Admin always has full access. Approval controls follow the Maker–Checker rule regardless of these settings.</p>

      <div className="grid md:grid-cols-[240px,1fr] gap-5">
        <div className="bg-white rounded-2xl soft-shadow p-2 h-fit">
          {Object.entries(cfg.roles).map(([k, label]) => (
            <button key={k} onClick={() => setActive(k)} data-testid={`role-tab-${k}`}
              className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-500 mb-0.5 flex items-center justify-between transition-colors ${active === k ? "bg-forest text-cream" : "text-brown/80 hover:bg-sand"}`}>
              <span>{label}</span>{k === "super_admin" && <Lock className="w-3.5 h-3.5 opacity-70" />}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl soft-shadow p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-xl text-forest">{cfg.roles[active]}</h2>
              <p className="text-xs text-brown/60">{isSuper ? "Full access — cannot be modified." : `${(perms[active] || []).length} permissions enabled`}</p>
            </div>
            {!isSuper && <button onClick={resetToDefault} className="text-xs text-olive hover:text-forest inline-flex items-center gap-1" data-testid="reset-role-btn"><RotateCcw className="w-3.5 h-3.5" /> Reset to default</button>}
          </div>

          <div className="space-y-5">
            {cfg.permission_groups.map((g) => (
              <div key={g.group}>
                <p className="text-[11px] uppercase tracking-wide text-olive font-600 mb-2">{g.group}</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {g.perms.map(([key, label]) => (
                    <label key={key} htmlFor={`${active}-${key}`} className={`flex items-center gap-2.5 rounded-xl px-3 py-2 border ${has(key) ? "border-sage bg-sage/10" : "border-stone-200 bg-white"} ${isSuper ? "opacity-70" : "cursor-pointer hover:border-sage"}`}>
                      <Checkbox id={`${active}-${key}`} checked={isSuper || has(key)} disabled={isSuper} onCheckedChange={() => toggle(key)} data-testid={`perm-${active}-${key}`} />
                      <span className="text-sm text-brown">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
